import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, ArrowLeft, Send, AlertCircle } from 'lucide-react';
import { Button, Input, Label, Alert, AlertDescription } from '@repo/ui';

const emailSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'validation.required' })
    .pipe(
      z
        .string()
        .regex(
          /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          'validation.invalidEmail'
        )
    ),
});

type EmailFormValues = z.infer<typeof emailSchema>;

interface EmailStepProps {
  onBack?: () => void;
  onSuccess: (email: string) => void;
}

export const EmailStep: React.FC<EmailStepProps> = ({ onBack, onSuccess }) => {
  const { t } = useTranslation();
  const { forgotPassword } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EmailFormValues>({
    // @ts-ignore
    resolver: async (values) => {
      const result = await emailSchema.safeParseAsync(values);
      if (result.success) return { values: result.data, errors: {} };
      const errors = result.error.issues.reduce((acc: any, issue) => {
        acc[issue.path[0] as string] = { type: issue.code, message: issue.message };
        return acc;
      }, {});
      return { values: {}, errors };
    },
    defaultValues: { email: '' },
    mode: 'onChange',
  });

  const onSubmit = async (data: EmailFormValues) => {
    setError(null);
    try {
      await forgotPassword(data.email);
      onSuccess(data.email);
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.failedToSendOtp'));
    }
  };

  return (
    <div className="w-full">
      {onBack && (
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-6 cursor-pointer pl-0 hover:bg-transparent hover:text-foreground text-muted-foreground self-start"
        >
          <ArrowLeft size={18} className="ltr:mr-2 rtl:ml-2 rtl:rotate-180" />
          {t('auth.backToLogin')}
        </Button>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <Label htmlFor="email" className="mb-1.5 block">
            {t('auth.email')}
          </Label>
          <Input
            {...register('email')}
            id="email"
            type="email"
            placeholder={t('auth.emailPlaceholder')}
            icon={<Mail className="h-4 w-4" />}
            error={!!errors.email}
            className="h-11"
          />
          {errors.email && (
            <div className="flex items-center gap-2 text-red-500 mt-1.5 pl-1 fade-in slide-in-from-top-1 duration-200">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm font-medium">{t(errors.email.message as string)}</p>
            </div>
          )}
        </div>

        {error && (
          <Alert variant="error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full p bg-[oklch(0.34_0.17_262.91)] hover:bg-[oklch(0.34_0.17_262.91)]/90 text-white shadow-lg shadow-[oklch(0.34_0.17_262.91)]/20 -mt-3 cursor-pointer"
          size="sm"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mx-2" />
              {t('auth.sending')}
            </>
          ) : (
            <>
              <Send className="h-5 w-5 ltr:mr-2 rtl:ml-2" />
              {t('auth.sendVerificationCode')}
            </>
          )}
        </Button>
      </form>
    </div>
  );
};
